import api from './api';
import type { Decision } from './sessions';

export type SanctionType = 'ban_temp' | 'ban_perm' | 'kick' | 'warn';
// Fase 3 (2026-08-07) — `'user'` acrescentado. Não é campo novo: o backend já
// aceitava (`SanctionService.cpp::IsValidTargetKind`) e o CHECK da tabela
// `sanctions` também. O tipo do painel é que estava desatualizado, e isso
// bloqueava a fila de ban de sancionar um alvo `user` — que é a maioria dos
// alvos identificáveis hoje, com `account_id` em 0 de 45 sessões.
export type TargetKind = 'hwid' | 'account' | 'ip' | 'session_uuid' | 'user';

export interface Sanction {
  id: string;
  sanction_type: SanctionType;
  target_kind: TargetKind;
  target_value: string;
  source_session_uuid: string;
  reason: string;
  active_until: string;
  created_at: string;
  reverted_at: string;
  revert_reason: string;
  is_active: boolean;
}

export interface PendingReviewRow {
  session_uuid: string;
  account_id: string;
  user_id: string;
  hwid: string;
  ip: string;
  risk_score: number;
  peak_risk_score: number;
  incident_count: number;
  occurrence_sum: number;
  decision: Decision;
  decision_real: Decision;
  peak_decision_real: Decision;
  last_heartbeat: string;
  already_sanctioned: boolean;
}

export interface CreateSanctionInput {
  sanction_type: SanctionType;
  target_kind: TargetKind;
  target_value: string;
  reason?: string;
  source_session_uuid?: string;
  active_until_epoch?: number;
}

export interface BulkResult {
  created: number;
  skipped_duplicate: number;
  failed: number;
  created_uuids: string[];
}

export async function listSanctions(includeReverted = false, limit = 50): Promise<Sanction[]> {
  const resp = await api.get<Sanction[]>('/admin/sanctions', {
    params: {
      include_reverted: includeReverted ? 'true' : undefined,
      limit,
    },
  });
  return resp.data;
}

export async function listPending(riskThreshold = 50, limit = 50): Promise<PendingReviewRow[]> {
  const resp = await api.get<PendingReviewRow[]>('/admin/sanctions/pending', {
    params: { risk_threshold: riskThreshold, limit },
  });
  return resp.data;
}

export async function createSanction(input: CreateSanctionInput): Promise<Sanction> {
  const resp = await api.post<Sanction>('/admin/sanctions', input);
  return resp.data;
}

export async function bulkSanctions(inputs: CreateSanctionInput[]): Promise<BulkResult> {
  const resp = await api.post<BulkResult>('/admin/sanctions/bulk', {
    sanctions: inputs,
  });
  return resp.data;
}

export async function revertSanction(uuid: string, revertReason: string): Promise<Sanction> {
  const resp = await api.post<Sanction>(`/admin/sanctions/${uuid}/revert`, {
    revert_reason: revertReason,
  });
  return resp.data;
}

// W6 corte 16.C — GET /admin/sanctions/stats — métricas globais de ban wave.
// Shape definido em SanctionService::StatsReport::ToJson.
export interface SanctionTypeCount {
  sanction_type: SanctionType;
  count: number;
}

export interface TargetKindCount {
  target_kind: TargetKind;
  count: number;
}

export interface SanctionStats {
  total_count: number;
  active_count: number;
  reverted_count: number;
  created_last_24h: number;
  reverted_last_24h: number;
  by_sanction_type: SanctionTypeCount[];
  by_target_kind:   TargetKindCount[];
  // 0..1 — só presente quando total_count > 0 (revertidas / total).
  fp_rate?: number;
}

export async function getSanctionStats(): Promise<SanctionStats> {
  const resp = await api.get<SanctionStats>('/admin/sanctions/stats');
  return resp.data;
}
